/**
 * Roles whose assignment through the global UserRole relation would grant authority that bypasses
 * normal feature or scope checks. Some entries are product-scoped, but delegated assignment of
 * any one is still a privilege escalation.
 */
export const PLATFORM_GLOBAL_ROLE_NAMES = ['SYSTEM_ADMIN', 'platform.superadmin'];
/**
 * Roles granted a `['*']` wildcard permission bundle by the seeded access-control
 * permission matrices under `libs/prisma/access-control-client/prisma/seeds`. This
 * list is the single source of truth for those seeds and is verified against them
 * by `protected-global-role.spec.ts` — do not add a role here unless a seed
 * permission matrix actually grants it `['*']`, and do not drop one that does.
 */
export const PERMISSION_MATRIX_WILDCARD_ROLE_NAMES = [
    'CMP_ADMIN',
    'TENANT_OWNER',
    'TENANT_ADMIN',
    'OWNER',
    'Publisher',
];
/**
 * Global roles with privileged product admission that is not represented by a
 * seed permission-matrix wildcard. They remain protected from delegated
 * global assignment for the same escalation reason as wildcard roles.
 */
export const DIRECT_PRIVILEGED_GLOBAL_ROLE_NAMES = ['facilities.admin'];
export const SCOPE_BYPASS_ROLE_NAMES = [
    'ADMIN',
    'SYSTEM_ADMIN',
    'platform.superadmin',
    'access-control.admin',
    'VOICEPRO_EDITOR',
    'OWNER',
];
/**
 * Product guards that map these role names to a product-wide wildcard. The assignment boundary
 * is global, so a delegated role writer must not be able to grant them to itself.
 */
export const PRODUCT_WILDCARD_ROLE_NAMES = ['Admin', 'VOICEPRO_ADMIN'];
/**
 * Roles whose wildcard authority is conferred through a product audience policy rather
 * than a seed permission matrix or a product guard. `facilities.admin` is granted the
 * `['*']` Facilities bundle by `FACILITIES_BASE_ROLE_BUNDLES`
 * (`libs/prisma/access-control-client/src/seed/facilities-authorization-contract.ts`)
 * and is elevated to full Facilities access by the `facilities-admin` audience policy
 * (`libs/auth/src/lib/audience/policies.ts`). Neither source is scanned by the seed
 * permission-matrix guard, so it is protected here to keep delegated assignment blocked.
 */
export const AUDIENCE_WILDCARD_ROLE_NAMES = ['facilities.admin'];
function uniqueRoleNames(roleNames) {
    const normalizedRoleNames = new Set();
    return roleNames.filter((roleName) => {
        const normalizedRoleName = roleName.toLowerCase();
        if (normalizedRoleNames.has(normalizedRoleName)) {
            return false;
        }
        normalizedRoleNames.add(normalizedRoleName);
        return true;
    });
}
export const PROTECTED_GLOBAL_ROLE_NAMES = uniqueRoleNames([
    ...PLATFORM_GLOBAL_ROLE_NAMES,
    ...PERMISSION_MATRIX_WILDCARD_ROLE_NAMES,
    ...DIRECT_PRIVILEGED_GLOBAL_ROLE_NAMES,
    ...SCOPE_BYPASS_ROLE_NAMES,
    ...PRODUCT_WILDCARD_ROLE_NAMES,
    ...AUDIENCE_WILDCARD_ROLE_NAMES,
]);
/** The sole authority permitted to assign a role from the protected inventory. */
export const PLATFORM_SUPERADMIN_ROLE = 'platform.superadmin';
const PROTECTED_GLOBAL_ROLE_NAME_SET = new Set(PROTECTED_GLOBAL_ROLE_NAMES.map((roleName) => roleName.toLowerCase()));
const NORMALIZED_SCOPE_BYPASS_ROLE_NAME_SET = new Set(SCOPE_BYPASS_ROLE_NAMES.map((roleName) => roleName.toLowerCase()));
export function isProtectedGlobalRoleName(roleName) {
    return roleName !== undefined && PROTECTED_GLOBAL_ROLE_NAME_SET.has(roleName.toLowerCase());
}
export function isScopeBypassRoleName(roleName) {
    return NORMALIZED_SCOPE_BYPASS_ROLE_NAME_SET.has(roleName.toLowerCase());
}
//# sourceMappingURL=protected-global-role.js.map